import { CHUNK_HEIGHT, CHUNK_SIZE } from "../config";
import { Block, isLiquid, isSolid } from "./blocks";
import {
  Chunk,
  chunkKey,
  parseChunkKey,
  worldToChunk,
  worldToLocal,
} from "./chunk";

export class World {
  readonly seed: number;
  readonly chunks = new Map<string, Chunk>();
  readonly edits = new Map<string, Map<number, number>>();

  constructor(seed: number) {
    this.seed = seed;
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(chunkKey(cx, cz));
  }

  ensureChunk(cx: number, cz: number): Chunk {
    const key = chunkKey(cx, cz);
    let c = this.chunks.get(key);
    if (!c) {
      c = new Chunk(cx, cz);
      this.chunks.set(key, c);
    }
    return c;
  }

  getBlock(x: number, y: number, z: number): number {
    if (y < 0 || y >= CHUNK_HEIGHT) return Block.AIR;
    const chunk = this.getChunk(worldToChunk(x), worldToChunk(z));
    if (!chunk) return Block.AIR;
    return chunk.get(worldToLocal(x), y, worldToLocal(z));
  }

  setBlock(x: number, y: number, z: number, id: number): boolean {
    if (y < 0 || y >= CHUNK_HEIGHT) return false;
    const cx = worldToChunk(x);
    const cz = worldToChunk(z);
    const chunk = this.ensureChunk(cx, cz);
    const lx = worldToLocal(x);
    const lz = worldToLocal(z);
    const prev = chunk.get(lx, y, lz);
    if (prev === id) return false;
    if (prev === Block.BEDROCK) return false;
    chunk.set(lx, y, lz, id);
    this.recordEdit(cx, cz, lx, y, lz, id);
    this.markNeighborsDirty(cx, cz, lx, lz);
    return true;
  }

  isSolid(x: number, y: number, z: number): boolean {
    return isSolid(this.getBlock(Math.floor(x), Math.floor(y), Math.floor(z)));
  }

  isWater(x: number, y: number, z: number): boolean {
    return isLiquid(this.getBlock(Math.floor(x), Math.floor(y), Math.floor(z)));
  }

  biomeAt(x: number, z: number): number {
    const chunk = this.getChunk(worldToChunk(x), worldToChunk(z));
    if (!chunk) return 0;
    return chunk.biomeAt(worldToLocal(x), worldToLocal(z));
  }

  applyGenerated(cx: number, cz: number, blocks: Uint16Array, biomes: Uint8Array, heights: Uint8Array): Chunk {
    const chunk = this.ensureChunk(cx, cz);
    chunk.adopt(blocks, biomes, heights);
    const edits = this.edits.get(chunkKey(cx, cz));
    if (edits) {
      for (const [index, id] of edits) {
        chunk.blocks[index] = id;
      }
    }
    chunk.dirty = true;
    return chunk;
  }

  unloadFar(px: number, pz: number, keep: number): string[] {
    const pcx = worldToChunk(px);
    const pcz = worldToChunk(pz);
    const removed: string[] = [];
    for (const [key, chunk] of this.chunks) {
      if (Math.max(Math.abs(chunk.cx - pcx), Math.abs(chunk.cz - pcz)) > keep) {
        this.chunks.delete(key);
        removed.push(key);
      }
    }
    return removed;
  }

  private recordEdit(cx: number, cz: number, lx: number, y: number, lz: number, id: number): void {
    const key = chunkKey(cx, cz);
    let map = this.edits.get(key);
    if (!map) {
      map = new Map();
      this.edits.set(key, map);
    }
    map.set(lx + lz * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE, id);
  }

  private markNeighborsDirty(cx: number, cz: number, lx: number, lz: number): void {
    const mark = (nx: number, nz: number) => {
      const n = this.getChunk(nx, nz);
      if (n) n.dirty = true;
    };
    if (lx === 0) mark(cx - 1, cz);
    if (lx === CHUNK_SIZE - 1) mark(cx + 1, cz);
    if (lz === 0) mark(cx, cz - 1);
    if (lz === CHUNK_SIZE - 1) mark(cx, cz + 1);
  }
}

export { chunkKey, parseChunkKey, worldToChunk, worldToLocal };
